const initProductTabs = (container) => {
  const tabs = Array.from(container.querySelectorAll('[role="tab"]'));
  const panels = Array.from(container.querySelectorAll('[role="tabpanel"]'));
  if (!tabs.length) return;

  const activateTab = (tab, moveFocus = true) => {
    tabs.forEach((item) => {
      const selected = item === tab;
      item.setAttribute('aria-selected', selected ? 'true' : 'false');
      item.tabIndex = selected ? 0 : -1;
    });

    panels.forEach((panel) => {
      panel.hidden = panel.id !== tab.getAttribute('aria-controls');
    });

    if (moveFocus) tab.focus();
  };

  tabs.forEach((tab, index) => {
    tab.addEventListener('click', () => activateTab(tab, false));
    tab.addEventListener('keydown', (event) => {
      let nextIndex = null;
      if (event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length;
      if (event.key === 'ArrowLeft') nextIndex = (index - 1 + tabs.length) % tabs.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = tabs.length - 1;
      if (nextIndex === null) return;
      event.preventDefault();
      activateTab(tabs[nextIndex]);
    });
  });
};

const initProductGallery = (gallery) => {
  const panels = Array.from(gallery.querySelectorAll('[data-product-media]'));
  const triggers = Array.from(gallery.querySelectorAll('[data-product-media-trigger]'));
  if (!panels.length || !triggers.length) return;

  const activateMedia = (mediaId) => {
    panels.forEach((panel) => {
      const active = panel.dataset.productMedia === mediaId;
      panel.hidden = !active;
      if (!active) panel.querySelector('video')?.pause();
    });

    triggers.forEach((trigger) => {
      trigger.setAttribute('aria-current', trigger.dataset.productMediaTrigger === mediaId ? 'true' : 'false');
    });
  };

  triggers.forEach((trigger) => {
    trigger.addEventListener('click', () => activateMedia(trigger.dataset.productMediaTrigger));
  });
};

const syncQuantitySelector = (selector) => {
  const input = selector.querySelector('input[type="number"]');
  const decrement = selector.querySelector('[data-quantity-decrement]');
  const increment = selector.querySelector('[data-quantity-increment]');
  if (!input || !decrement || !increment) return;

  const value = Number.parseInt(input.value, 10) || 1;
  const min = input.min === '' ? 1 : Number.parseInt(input.min, 10);
  const max = input.max === '' ? null : Number.parseInt(input.max, 10);

  decrement.disabled = value <= min;
  increment.disabled = max !== null && value >= max;
};

const initQuantitySelector = (selector) => {
  const input = selector.querySelector('input[type="number"]');
  if (!input) return;

  const changeBy = (direction) => {
    const step = Number.parseInt(input.step, 10) || 1;
    const min = input.min === '' ? 1 : Number.parseInt(input.min, 10);
    const max = input.max === '' ? null : Number.parseInt(input.max, 10);
    const current = Number.parseInt(input.value, 10) || min;
    let next = current + direction * step;

    next = Math.max(min, next);
    if (max !== null) next = Math.min(max, next);

    input.value = String(next);
    input.dispatchEvent(new Event('change', { bubbles: true }));
    syncQuantitySelector(selector);
  };

  selector.querySelector('[data-quantity-decrement]')?.addEventListener('click', () => changeBy(-1));
  selector.querySelector('[data-quantity-increment]')?.addEventListener('click', () => changeBy(1));
  input.addEventListener('input', () => syncQuantitySelector(selector));
  input.addEventListener('change', () => {
    const min = input.min === '' ? 1 : Number.parseInt(input.min, 10);
    const max = input.max === '' ? null : Number.parseInt(input.max, 10);
    let value = Number.parseInt(input.value, 10) || min;
    value = Math.max(min, value);
    if (max !== null) value = Math.min(max, value);
    input.value = String(value);
    syncQuantitySelector(selector);
  });

  syncQuantitySelector(selector);
};

const extractDescriptionRow = (element) => {
  const firstChild = element.firstElementChild;
  if (firstChild && (firstChild.tagName === 'STRONG' || firstChild.tagName === 'B')) {
    const label = firstChild.textContent.trim().replace(/[:\s]+$/, '');
    const clone = element.cloneNode(true);
    const clonedLabel = clone.firstElementChild;
    if (clonedLabel) clonedLabel.remove();
    const value = clone.innerHTML.replace(/^\s*[:\-–—]\s*/, '').trim();
    if (label && value) return { label, value };
  }

  const text = element.textContent.trim();
  const separatorIndex = text.indexOf(':');
  if (separatorIndex > 0 && separatorIndex < 45) {
    const label = text.slice(0, separatorIndex).trim();
    const value = text.slice(separatorIndex + 1).trim();
    if (label && value) return { label, value };
  }

  return null;
};

const enhanceProductDescription = (root) => {
  if (!root || root.dataset.tableEnhanced === 'true') return;
  root.dataset.tableEnhanced = 'true';

  const rows = [];
  const removable = [];
  let insertionPoint = null;

  Array.from(root.children).forEach((child) => {
    if (child.matches('p')) {
      const row = extractDescriptionRow(child);
      if (row) {
        rows.push(row);
        removable.push(child);
        insertionPoint ||= child;
      }
      return;
    }

    if (child.matches('ul,ol')) {
      const items = Array.from(child.children).filter((item) => item.matches('li'));
      const listRows = items.map(extractDescriptionRow);
      if (items.length >= 2 && listRows.every(Boolean)) {
        rows.push(...listRows);
        removable.push(child);
        insertionPoint ||= child;
      }
    }
  });

  if (rows.length < 2 || !insertionPoint) return;

  const table = document.createElement('div');
  table.className = 'dt-spec-table';
  table.setAttribute('role', 'table');
  table.setAttribute('aria-label', 'Product specifications');

  rows.forEach(({ label, value }) => {
    const row = document.createElement('div');
    row.className = 'dt-spec-row';
    row.setAttribute('role', 'row');

    const key = document.createElement('div');
    key.className = 'dt-spec-key';
    key.setAttribute('role', 'rowheader');
    key.textContent = label;

    const cell = document.createElement('div');
    cell.className = 'dt-spec-value';
    cell.setAttribute('role', 'cell');
    cell.innerHTML = value;

    row.append(key, cell);
    table.appendChild(row);
  });

  root.insertBefore(table, insertionPoint);
  removable.forEach((node) => node.remove());
};

document.querySelectorAll('[data-product-tabs]').forEach(initProductTabs);
document.querySelectorAll('[data-product-gallery]').forEach(initProductGallery);
document.querySelectorAll('[data-quantity-selector]').forEach(initQuantitySelector);
document.querySelectorAll('[data-product-description]').forEach(enhanceProductDescription);
